/**
 * Runtime asset loading.
 *
 * Everything the game needs is described by the generated manifest (see manifest.ts for why
 * it must exist at all). Nothing here scans, guesses, or derives a path — if an asset is not
 * in the manifest, it does not exist as far as the runtime is concerned.
 *
 * Assets are keyed by the ORIGINAL path the C++ used, e.g. "media/objects/balls/generic.ase",
 * so ported code can ask for what it asked for before.
 */

import {
  LoadingManager,
  NoColorSpace,
  RepeatWrapping,
  SRGBColorSpace,
  Texture,
  TextureLoader,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { isManifest, type AssetManifest } from './manifest';

export interface LoadProgress {
  loaded: number;
  total: number;
  /** 0..1. Byte-weighted where possible, item-weighted otherwise. */
  fraction: number;
  current: string;
}

export class AssetStore {
  readonly manifest: AssetManifest;
  private readonly root: string;
  private readonly gltf: GLTFLoader;
  private readonly textures: TextureLoader;

  private readonly meshCache = new Map<string, Promise<GLTF>>();
  private readonly textureCache = new Map<string, Promise<Texture>>();
  private readonly audioCache = new Map<string, Promise<ArrayBuffer>>();
  private animationBuffer: ArrayBuffer | null = null;

  /**
   * Substituted for any texture the manifest marks as absent. The original treats a missing
   * texture as fatal (imageloader.cpp:22) and two of its own meshes reference files that do
   * not exist, so it only survives because those materials are never instantiated. We refuse
   * to inherit that: a missing texture is visible, not fatal.
   */
  private missingTexture: Texture | null = null;

  private constructor(manifest: AssetManifest, root: string) {
    this.manifest = manifest;
    this.root = root.endsWith('/') ? root : `${root}/`;
    const manager = new LoadingManager();
    this.gltf = new GLTFLoader(manager);
    this.textures = new TextureLoader(manager);
  }

  static async open(root = '/assets'): Promise<AssetStore> {
    const url = `${root.replace(/\/$/, '')}/manifest.json`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Asset manifest not found at ${url} (${response.status}). ` +
          `Run: npm run assets`,
      );
    }
    const data: unknown = await response.json();
    if (!isManifest(data)) {
      throw new Error(
        `Asset manifest at ${url} does not match the expected contract. ` +
          `It was probably produced by an older conversion run — re-run: npm run assets`,
      );
    }
    return new AssetStore(data, root);
  }

  private url(relative: string): string {
    return `${this.root}${relative}`;
  }

  // -------------------------------------------------------------------------
  // Meshes
  // -------------------------------------------------------------------------

  /** @param key original path, e.g. "media/objects/balls/generic.ase" */
  async mesh(key: string): Promise<GLTF> {
    const cached = this.meshCache.get(key);
    if (cached) return cached;

    const entry = this.manifest.meshes[key];
    if (!entry) {
      throw new Error(
        `No mesh "${key}" in the manifest. ` +
          `Known keys look like "media/objects/balls/generic.ase".`,
      );
    }

    const promise = this.gltf.loadAsync(this.url(entry.file));
    this.meshCache.set(key, promise);
    return promise;
  }

  // -------------------------------------------------------------------------
  // Textures
  // -------------------------------------------------------------------------

  /**
   * @param colour true for textures that carry COLOUR (albedo/diffuse), false for textures
   *        that carry DATA (normal, specular, illumination maps).
   *
   * This distinction is not cosmetic. The original uploads diffuse maps as sRGB
   * (`graphics_geometry.cpp:89` — SRGB8 / SRGBA8) so the GPU linearises them on sample, and
   * uploads normal, specular and illumination maps as plain RGB8 because they are data, not
   * colour (`graphics_geometry.cpp:107,125,143`). The pitch follows the same split
   * (`proceduralpitch.cpp:363,370,377`).
   *
   * Getting this wrong is invisible in any single texture and wrecks the whole image:
   * sampling an sRGB-encoded albedo as if it were linear makes it roughly 2.2x too bright
   * before lighting is applied at all, and the frame blows out to pale yellow.
   */
  async texture(key: string, colour = true): Promise<Texture> {
    const cacheKey = `${colour ? 'c' : 'd'}:${key}`;
    const cached = this.textureCache.get(cacheKey);
    if (cached) return cached;

    const entry = this.manifest.textures[key];
    if (!entry || entry.missing) {
      if (!entry) console.warn(`[assets] texture "${key}" is not in the manifest`);
      return this.getMissingTexture();
    }

    const promise = this.textures.loadAsync(this.url(entry.file)).then((texture) => {
      texture.colorSpace = colour ? SRGBColorSpace : NoColorSpace;
      texture.flipY = false;
      // Every mesh texture in the original tiles: `bool repeat = true`, hard-coded at
      // graphics_geometry.cpp:85 and passed straight through to GL_REPEAT
      // (opengl_renderer3d.cpp:1521).
      //
      // This is load-bearing, not a detail. The mesh loader NEGATES the V coordinate
      // (aseloader.cpp:288), so a mesh authored with V in 0..1 arrives with V in -1..0 and
      // only lands back on the image because the sampler wraps. three.js defaults to
      // clamping instead, which pins every one of those meshes to the texture's top row —
      // a player's shirt comes out a flat block of colour, with no error anywhere.
      texture.wrapS = RepeatWrapping;
      texture.wrapT = RepeatWrapping;
      return texture;
    });
    this.textureCache.set(cacheKey, promise);
    return promise;
  }

  private async getMissingTexture(): Promise<Texture> {
    if (this.missingTexture) return this.missingTexture;
    // A 2x2 magenta/black check — loud enough to notice in a screenshot, cheap enough to
    // build without a network request.
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 2;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ff00ff';
    ctx.fillRect(0, 0, 2, 2);
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, 1, 1);
    ctx.fillRect(1, 1, 1, 1);
    this.missingTexture = new Texture(canvas);
    this.missingTexture.needsUpdate = true;
    return this.missingTexture;
  }

  // -------------------------------------------------------------------------
  // Audio
  // -------------------------------------------------------------------------

  /** @param key original path, e.g. "media/sounds/ballsound.wav" */
  audio(key: string): Promise<ArrayBuffer> {
    const cached = this.audioCache.get(key);
    if (cached) return cached;

    const entry = this.manifest.audio[key];
    if (!entry) {
      throw new Error(
        `No audio "${key}" in the manifest. ` +
          `Known keys look like "media/sounds/ballsound.wav".`,
      );
    }

    const promise = fetch(this.url(entry.file)).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Audio data missing at ${entry.file} (${response.status})`);
      }
      return response.arrayBuffer();
    });
    this.audioCache.set(key, promise);
    return promise;
  }

  // -------------------------------------------------------------------------
  // Skeleton, poses, skinning, database
  // -------------------------------------------------------------------------

  /**
   * Small JSON side files, fetched once and cached.
   *
   * These are the pieces the original reads from formats a browser has no business parsing
   * at runtime — the skeleton from an XML-ish `.object`, the reference poses from a
   * comma-separated text format, the squads from a SQLite file.
   */
  private readonly jsonCache = new Map<string, Promise<unknown>>();

  private json<T>(relative: string): Promise<T> {
    const cached = this.jsonCache.get(relative);
    if (cached) return cached as Promise<T>;
    const promise = fetch(this.url(relative)).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Asset ${relative} missing (${response.status}). Run: npm run assets`);
      }
      return response.json() as Promise<T>;
    });
    this.jsonCache.set(relative, promise);
    return promise as Promise<T>;
  }

  /** The player skeleton, converted from player.object. */
  skeleton<T>(): Promise<T> {
    return this.json<T>(this.manifest.skeleton.file);
  }

  /**
   * A reference pose, by the original's own path — for example
   * "media/animations/base.anim.util". Two of these define how the body mesh binds to the
   * skeleton; see render/humanoid.ts.
   */
  restPose<T>(originalPath: string): Promise<T> {
    const file = this.manifest.skeleton.restPoseFiles[originalPath];
    if (!file) {
      throw new Error(
        `No reference pose "${originalPath}". Known: ` +
          Object.keys(this.manifest.skeleton.restPoseFiles).join(', '),
      );
    }
    return this.json<T>(file);
  }

  /** Bone weights for a skinned mesh. There is one: "fullbody". */
  skin<T>(name: string): Promise<T> {
    const entry = this.manifest.skinning[name];
    if (!entry) throw new Error(`No skinning data named "${name}"`);
    return this.json<T>(entry.file);
  }

  /** Teams, squads and formations, converted out of the original's SQLite file. */
  database<T>(): Promise<T> {
    return this.json<T>(this.manifest.database.file);
  }

  // -------------------------------------------------------------------------
  // Animations
  // -------------------------------------------------------------------------

  /**
   * All clips live in one binary blob; each manifest entry carries its offset and length.
   * One request rather than 293.
   */
  /**
   * The animation buffer's own index: joint order, per-track key counts, byte ranges, and
   * every clip's variables and pre-baked derived properties.
   *
   * Separate from the manifest on purpose. The manifest answers "which clips exist and which
   * file are their bytes in", which is enough to fetch the buffer; this answers "how are the
   * bytes laid out, and what does each clip mean", which is what clip SELECTION and DECODING
   * both need. Keeping the binary format's description next to the binary means the format
   * can change without touching the manifest contract — and it means a clip's variables live
   * in exactly one file rather than being duplicated into the manifest as well.
   */
  animationIndex<T>(): Promise<T> {
    return this.json<T>('animations/index.json');
  }

  async animationData(): Promise<ArrayBuffer> {
    if (this.animationBuffer) return this.animationBuffer;
    const first = this.manifest.animations[0];
    if (!first) throw new Error('Manifest contains no animations');
    const response = await fetch(this.url(first.file));
    if (!response.ok) {
      throw new Error(`Animation data missing at ${first.file} (${response.status})`);
    }
    this.animationBuffer = await response.arrayBuffer();
    return this.animationBuffer;
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  /**
   * Warm everything needed before the first frame. The manifest names this set; we do not
   * guess it here.
   */
  async preloadBootSet(onProgress?: (p: LoadProgress) => void): Promise<void> {
    const keys = this.manifest.bootSet;
    let loaded = 0;

    for (const key of keys) {
      onProgress?.({ loaded, total: keys.length, fraction: loaded / keys.length, current: key });
      try {
        if (this.manifest.meshes[key]) await this.mesh(key);
        else if (this.manifest.textures[key]) await this.texture(key);
      } catch (error) {
        // A boot asset that fails should degrade the picture, not prevent the game starting.
        console.warn(`[assets] boot asset "${key}" failed to load`, error);
      }
      loaded++;
    }

    onProgress?.({ loaded, total: keys.length, fraction: 1, current: '' });
  }
}
